import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCategoryStyle } from '../../../../../utils/inventory_helper';
import { useInventory } from '../../../../../contexts/InventoryContext';
import ConfigurationOverview from './ConfigurationOverview';
import UnitsTable from '../../project_master/UnitsTable';
import MediaAndDocuments from '../../../../../components/inventory/MediaAndDocuments';
import AddNewUnit from '../../AddNewUnit';
import CSVImportUnits from '../../../components/CSVImportUnits';

const ConfigurationDetailsBase = ({ projectId, configurationId, config, project, onConfigUpdated, loadConfig }) => {
  const navigate = useNavigate();
  const { createUnit, inventoryOptions, updateConfiguration, fetchUnitUiSchema, fetchUnitsByConfiguration, updateUnit } = useInventory();
  const [tab, setTab] = useState('overview');
  const [addUnitDrawerOpen, setAddUnitDrawerOpen] = useState(false);
  const [unitDrawerUiSchema, setUnitDrawerUiSchema] = useState(null);
  const [importUnitsCSVOpen, setImportUnitsCSVOpen] = useState(false);


  useEffect(() => {
    if (!addUnitDrawerOpen || !fetchUnitUiSchema) {
      setUnitDrawerUiSchema(null);
      return;
    }
    const c = config || {};
    const propertyTypeId =
      c.property_type_id ?? (typeof c.property_type === 'object' ? c.property_type?.id : c.property_type);
    const categoryId = c.category_id ?? (typeof c.category === 'object' ? c.category?.id : c.category);
    fetchUnitUiSchema({ property_type_id: propertyTypeId, category_id: categoryId })
      .then((data) => setUnitDrawerUiSchema(data?.unit_schema ?? null))
      .catch(() => setUnitDrawerUiSchema(null));
  }, [addUnitDrawerOpen, fetchUnitUiSchema, config?.id]);
  const [unitListRefreshTrigger, setUnitListRefreshTrigger] = useState(0);
  const [detailsEditing, setDetailsEditing] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [keyHighlightsEditing, setKeyHighlightsEditing] = useState(false);
  const [keyHighlightsSaving, setKeyHighlightsSaving] = useState(false);
  const [tagsEditing, setTagsEditing] = useState(false);
  const [tagsSaving, setTagsSaving] = useState(false);
  const [otherDetailsEditing, setOtherDetailsEditing] = useState(false);
  const [otherDetailsSaving, setOtherDetailsSaving] = useState(false);
  const [commissionEditing, setCommissionEditing] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceSort, setPriceSort] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Lazy load config when Overview tab is selected
  useEffect(() => {
    if (tab !== 'overview' || config != null || !projectId || !configurationId || !loadConfig) return;
    setOverviewLoading(true);
    loadConfig()
      .finally(() => setOverviewLoading(false));
  }, [tab, config, projectId, configurationId, loadConfig]);

  // Lazy load units when Units tab is selected
  useEffect(() => {
    if (tab !== 'units' || !projectId || !configurationId || !fetchUnitsByConfiguration) return;
    setUnitsLoading(true);
    fetchUnitsByConfiguration(projectId, configurationId)
      .then((list) => setUnits(Array.isArray(list) ? list : []))
      .catch(() => setUnits([]))
      .finally(() => setUnitsLoading(false));
  }, [tab, projectId, configurationId, unitListRefreshTrigger, fetchUnitsByConfiguration]);

  const handleUnitStatusChange = async (unit, newStatus) => {
    if (!projectId || !configurationId || !updateUnit) return;
    try {
      await updateUnit(projectId, configurationId, unit.id, { inventory_status: newStatus });
      setUnits((prev) => prev.map((u) => (u.id === unit.id ? { ...u, inventory_status: newStatus } : u)));
    } catch (_) {}
  };

  const categoryStyle = config?.category ? getCategoryStyle(config.category) : null;

  const handleSaveDetails = async (payload) => {
    if (!config?.id || !projectId || !updateConfiguration) return;
    setDetailsSaving(true);
    try {
      const body = {
        price_min: payload.price_min ?? null,
        price_max: payload.price_max ?? null,
        area_min: payload.area_min ?? null,
        area_max: payload.area_max ?? null,
        area_unit: payload.area_unit || null,
      };
      if (payload.construction_status !== undefined) body.construction_status = payload.construction_status || 'under_construction';
      if (payload.name !== undefined) body.name = (payload.name || '').trim() || config.name;
      if (payload.property_type != null) body.property_type = payload.property_type;
      if (payload.category !== undefined) body.category = payload.category;
      if (payload.pricing !== undefined) body.pricing = payload.pricing;
      const updated = await updateConfiguration(projectId, config.id, body);
      if (onConfigUpdated && updated) onConfigUpdated(updated);
      setDetailsEditing(false);
    } finally {
      setDetailsSaving(false);
    }
  };

  const handleSaveKeyHighlights = async (key_highlights) => {
    if (!config?.id || !projectId || !updateConfiguration) return;
    setKeyHighlightsSaving(true);
    try {
      const updated = await updateConfiguration(projectId, config.id, { key_highlights: key_highlights ?? [] });
      if (onConfigUpdated && updated) onConfigUpdated(updated);
      setKeyHighlightsEditing(false);
    } finally {
      setKeyHighlightsSaving(false);
    }
  };

  const handleSaveTags = async (tags) => {
    if (!config?.id || !projectId || !updateConfiguration) return;
    setTagsSaving(true);
    try {
      const updated = await updateConfiguration(projectId, config.id, { tags: tags ?? [] });
      if (onConfigUpdated && updated) onConfigUpdated(updated);
      setTagsEditing(false);
    } finally {
      setTagsSaving(false);
    }
  };

  const handleSaveOtherDetails = async (metadata) => {
    if (!config?.id || !projectId || !updateConfiguration) return;
    setOtherDetailsSaving(true);
    try {
      const updated = await updateConfiguration(projectId, config.id, { metadata: metadata ?? {} });
      if (onConfigUpdated && updated) onConfigUpdated(updated);
      setOtherDetailsEditing(false);
    } finally {
      setOtherDetailsSaving(false);
    }
  };

  const handleSaveCommission = async (value) => {
    if (!config?.id || !projectId || !updateConfiguration) return;
    setCommissionSaving(true);
    try {
      const num = value != null && value !== '' ? parseFloat(value) : null;
      const updated = await updateConfiguration(projectId, config.id, {
        commission_rate: num,
      });
      if (onConfigUpdated && updated) onConfigUpdated(updated);
      setCommissionEditing(false);
    } finally {
      setCommissionSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, width: '100%', alignSelf: 'flex-start' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/inventory/projects/${projectId}/configurations`)}
        sx={{ mb: 2, textTransform: 'none', color: 'success.main' }}
      >
        Back to Configurations
      </Button>

      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography variant="h5" fontWeight={700}>
              {config?.name ?? (overviewLoading ? 'Loading…' : 'Configuration')}
            </Typography>
            {config?.category && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontWeight: 600,
                  bgcolor: categoryStyle?.bg,
                  color: categoryStyle?.color,
                  border: '1px solid',
                  borderColor: categoryStyle?.border,
                }}
              >
                {config.category}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {project?.name ?? '—'}
          </Typography>
        </Box>
        {/* Action buttons: Edit, Import CSV, Add Config */}
        <Box sx={{ display: 'flex', gap: '7px', alignItems: 'center', flexShrink: 0 }}>
          {config != null && (
            <Box
              component="button"
              onClick={() => setAddUnitDrawerOpen(true)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                px: '11px', py: '5px', borderRadius: '8px',
                fontSize: '11.5px', fontWeight: 600, fontFamily: 'inherit',
                cursor: 'pointer', letterSpacing: '-0.1px',
                background: '#1a4731', color: '#fff', border: 'none',
                boxShadow: '0 1px 2px rgba(15,31,21,.06)',
                transition: 'all 150ms ease',
                '&:hover': { background: '#00a35c', boxShadow: '0 2px 8px rgba(15,31,21,.09)', transform: 'translateY(-1px)' },
              }}
            >
              + Add Unit
            </Box>
          )}
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: 'success.main' },
          '& .MuiTabs-indicator': { bgcolor: 'success.main' },
        }}
      >
        <Tab label="Overview" value="overview" />
        <Tab label="Units" value="units" />
        <Tab label="Media & Documents" value="documents" />
      </Tabs>

      <Box sx={{ width: '100%', alignSelf: 'flex-start' }}>
        {tab === 'overview' && (
          overviewLoading && !config ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, p: 3 }}>
              <CircularProgress size={32} sx={{ color: 'success.main' }} />
            </Box>
          ) : config ? (
          <ConfigurationOverview
            config={config}
            project={project}
            areaUnitOptions={inventoryOptions?.area_units ?? []}
            categoryOptions={inventoryOptions?.categories ?? []}
            propertyTypeOptions={inventoryOptions?.property_types ?? []}
            isDetailsEditing={detailsEditing}
            onEditDetails={() => setDetailsEditing(true)}
            onSaveDetails={handleSaveDetails}
            onCancelDetails={() => setDetailsEditing(false)}
            detailsSaving={detailsSaving}
            isKeyHighlightsEditing={keyHighlightsEditing}
            onEditKeyHighlights={() => setKeyHighlightsEditing(true)}
            onSaveKeyHighlights={handleSaveKeyHighlights}
            onCancelKeyHighlights={() => setKeyHighlightsEditing(false)}
            keyHighlightsSaving={keyHighlightsSaving}
            isTagsEditing={tagsEditing}
            onEditTags={() => setTagsEditing(true)}
            onSaveTags={handleSaveTags}
            onCancelTags={() => setTagsEditing(false)}
            tagsSaving={tagsSaving}
            isOtherDetailsEditing={otherDetailsEditing}
            onEditOtherDetails={() => setOtherDetailsEditing(true)}
            onSaveOtherDetails={handleSaveOtherDetails}
            onCancelOtherDetails={() => setOtherDetailsEditing(false)}
            otherDetailsSaving={otherDetailsSaving}
            isCommissionEditing={commissionEditing}
            onEditCommission={() => setCommissionEditing(true)}
            onSaveCommission={handleSaveCommission}
            onCancelCommission={() => setCommissionEditing(false)}
            commissionSaving={commissionSaving}
          />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120, p: 3 }}>
              <Typography color="text.secondary">Unable to load configuration details.</Typography>
            </Box>
          )
        )}
        {tab === 'units' && (
          <UnitsTable
            projectId={projectId}
            configurationId={configurationId}
            config={config}
            project={project}
            units={units}
            loading={unitsLoading}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            priceSort={priceSort}
            onPriceSortChange={setPriceSort}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onImportCSV={() => setImportUnitsCSVOpen(true)}
            onAddUnit={() => setAddUnitDrawerOpen(true)}
            onUnitStatusChange={handleUnitStatusChange}
            unitStatusOptions={inventoryOptions?.unit_statuses ?? []}
          />
        )}
        {tab === 'documents' && <MediaAndDocuments projectId={projectId} configurationId={configurationId} config={config} />}
      </Box>

      <AddNewUnit
        open={addUnitDrawerOpen}
        onClose={() => setAddUnitDrawerOpen(false)}
        config={config}
        project={project}
        projectId={projectId}
        configurationId={configurationId}
        propertyTypes={inventoryOptions?.property_types ?? []}
        uiSchema={unitDrawerUiSchema}
        unitStatusOptions={inventoryOptions?.unit_statuses ?? []}
        facingOptions={inventoryOptions?.facing_directions ?? []}
        areaUnitOptions={inventoryOptions?.area_units ?? []}
        onSubmit={async (apiPayload) => {
          await createUnit(projectId, configurationId, apiPayload);
          setUnitListRefreshTrigger((t) => t + 1);
          setAddUnitDrawerOpen(false);
        }}
      />

      <CSVImportUnits
        open={importUnitsCSVOpen}
        onClose={() => setImportUnitsCSVOpen(false)}
        configurationName={config?.name}
        areaUnit={config?.area_unit || 'sqft'}
        onImport={async (apiPayloads) => {
          for (const payload of apiPayloads) {
            await createUnit(projectId, configurationId, payload);
          }
          setUnitListRefreshTrigger((t) => t + 1);
          setImportUnitsCSVOpen(false);
        }}
      />
    </Box>
  );
};

export default ConfigurationDetailsBase;
